import { Component, Injector, OnInit } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import { ExcelUtils } from 'src/app/classes/ExcelUtils';
import { Utils } from 'src/app/classes/Utils';
import {PaymentInfo} from 'src/app/models/models';
import {Bank_Movement, Payment, Store, User} from 'src/app/models/RestModels';
import {RestResponse, SearchObject} from 'src/app/services/Rest';
import {BaseComponent} from '../base/base.component';
import { PrinterService } from 'src/app/services/printer.service';

@Component({
	selector: 'app-list-payment-expenses',
	templateUrl: './list-payment-expenses.component.html',
	styleUrls: ['./list-payment-expenses.component.css']
})
export class ListPaymentExpensesComponent extends BaseComponent implements OnInit
{
	payment_search:SearchObject<Payment> = this.getEmptySearch();
	payment_info_list:PaymentInfo[] = [];
	user_list:User[] = [];
	store_list:Store[] = [];
	store_dictionary:Record<number,Store> = {};
	order_by_payment_id:Record<number,number> = {};

	fecha_inicial:string = '';
	fecha_final:string = '';

	//El backend une los LIKE con OR, por eso tag y concept comparten un solo campo de busqueda
	search_text:string = '';

	show_update_payment:boolean = false;
	selected_payment_info:PaymentInfo = null;

	private printerService: PrinterService;

	constructor(public injector: Injector) {
		super(injector);
		this.printerService = injector.get(PrinterService);
	}

	ngOnInit()
	{
		this.path = '/list-payment-expenses';

		//Los catalogos de los filtros se cargan una sola vez y aparte,
		//para no retrasar el listado de pagos mientras responden
		this.subs.sink = forkJoin
		({
			store: this.rest.getStores(true),
			user: this.rest.user.search
			({
				limit:9999,
				eq:{type:'USER'},
				sort_order:['name_ASC'],
				//Los proveedores tambien son type USER, se excluyen igual que en list-user
				search_extra:{ 'user_permission.is_provider': 0 }
			})
		})
		.subscribe
		({
			error:(error)=>this.showError(error),
			next:(responses)=>
			{
				this.store_list = responses.store.data;
				this.store_list.forEach((store)=>{ this.store_dictionary[ store.id ] = store });
				this.user_list = responses.user.data;
			}
		});

		this.sink= this.route.queryParamMap
		.pipe
		(
			mergeMap( (queryParamMap) =>
			{
				let fields =
				[
					"id","company_id","type","payment_amount","received_amount",
					"change_amount","currency_id","exchange_rate","received_by_user_id",
					"paid_by_user_id","created","updated","store_id","created_by_user_id",
					"tag","concept"
				];
				let extra_keys:Array<string> = []; //['search_param1','project_id','some_id'];
				this.payment_search = this.getSearch(queryParamMap, fields );

				this.setTitle('Lista de Pagos');
				this.current_page = this.payment_search.page;
				this.payment_search.eq.type = 'expense';
				this.payment_search.sort_order=['id_DESC'];

				if( !this.payment_search.ge.created )
				{
					let d = new Date();
					d.setDate( 1 );
					d.setHours( 0,0,0,0 );
					this.payment_search.ge.created = d;
				}

				if( !this.payment_search.le.created )
				{
					this.payment_search.le.created = Utils.getEndOfMonth( new Date() );
				}

				this.fecha_inicial = Utils.getLocalMysqlStringFromDate( this.payment_search.ge.created ).replace(' ','T');
				this.fecha_final = Utils.getLocalMysqlStringFromDate( this.payment_search.le.created ).replace(' ','T');
				this.search_text = this.payment_search.lk.tag || '';

				this.payment_info_list = [];
				this.is_loading = true;

				return this.rest.payment_info.search(this.payment_search)
			})
		)
		.subscribe
		({
			error:(error)=>this.showError(error),
			next:(responses)=>
			{
				responses.data.forEach((payment_info)=>
				{
					let ids = [];

					payment_info.movements.forEach((bmi)=>
					{
						bmi.bank_movement_orders.forEach((bmo)=>
						{
							ids.push( bmo.order_id );
						})
					})

					if( ids.length > 0 )
					{
						this.order_by_payment_id[ payment_info.payment.id ] = ids[0];
					}
				});

				this.payment_info_list = responses.data;
				this.setPages( this.payment_search.page, responses.total );
				this.is_loading = false;
			}
		});
	}

	fechaInicialChange(fecha:string)
	{
		this.fecha_inicial = fecha;
		this.payment_search.ge.created = fecha ? Utils.getDateFromLocalMysqlString( fecha ) : null;
	}

	fechaFinalChange(fecha:string)
	{
		this.fecha_final = fecha;
		this.payment_search.le.created = fecha ? Utils.getDateFromLocalMysqlString( fecha ) : null;
	}

	onSearchTextChange(search_text:string)
	{
		this.search_text = search_text;
		this.payment_search.lk.tag = search_text;
		this.payment_search.lk.concept = search_text;
	}

	imprimir(payment_info:PaymentInfo,evt:Event)
	{
		this.reprintExpenseUsb(payment_info, evt);
	}

	async reprintExpenseUsb(payment_info:PaymentInfo,evt:Event): Promise<void>
	{
		const prefs = this.printerService.resolvePreferences(this.rest.current_user);

		if (prefs.print_method !== 'USB' || !prefs.pos_printer_id)
		{
			Utils.openPrintTab('/#/print-payment-expense/'+payment_info.payment.id+'/print=1');
			return;
		}

		try
		{
			this.is_loading = true;

			if (this.printerService.available_printers.length === 0)
			{
				const storeId = payment_info.payment.store_id || this.rest.current_user?.store_id;
				if (storeId)
				{
					const response = await this.rest.printer.search({
						limit: 9999,
						eq: { store_id: storeId },
						sort_order: ['name_ASC']
					}).toPromise();
					this.printerService.available_printers = response.data;
				}
			}

			if (!this.printerService.isUsbPrintingEnabled())
			{
				Utils.openPrintTab('/#/print-payment-expense/'+payment_info.payment.id+'/print=1');
				return;
			}

			const result = await this.printerService.printExpense(payment_info, 1, evt);

			if (!result.success)
			{
				this.showError('Error al imprimir: ' + result.message);
				return;
			}

			this.showSuccess('Gasto impreso correctamente');
		}
		catch (error: any)
		{
			console.error('USB expense reprint failed:', error);
			this.showError('Error al imprimir: ' + (error?.message || 'Error desconocido'));
		}
		finally
		{
			this.is_loading = false;
		}
	}
	exportExcel()
	{
		let search:SearchObject<Payment> = {...this.payment_search};
		search.limit = 100;

		this.subs.sink = this.rest.payment_info
		.search( search ).pipe
		(
			mergeMap((response)=>
			{
				let observables:Observable<RestResponse<PaymentInfo>>[] = [ of(response) ];
				let pages= response.total/100;

				for(let i=1;i<pages;i++)
				{
					let s:SearchObject<Payment> = {...search};
					s.limit = 100;
					s.page = i;
					observables.push( this.rest.payment_info.search( s ) );
				}

				return forkJoin( observables );
			}),
			mergeMap((response)=>
			{
				let all:RestResponse<PaymentInfo> = { total: 0, data: []};

				response.forEach((i)=>{
					all.total = i.total;
					all.data.push(...i.data);
				})

				return of( all );
			})
		)
		.subscribe((response)=>
		{
			let toExport:Object[] = new Array();

			for(let pi of response.data )
			{
				let store = this.store_dictionary[ pi.payment.store_id ];

				let row = {
					Id: pi.payment.id,
					Cantidad: pi.payment.payment_amount,
					Tag: pi.payment.tag,
					Usuario: pi?.created_by_user?.name || '',
					Sucursal: store?.name || '',
					Concepto: pi.payment.concept,
					Fecha: Utils.getLocalMysqlStringFromDate( pi.payment.created )
				}
				toExport.push( row );
			}

			let headers = ['Id','Cantidad','Tag','Usuario','Sucursal','Concepto','Fecha'];
			ExcelUtils.array2xlsx(toExport,'Egresos.xlsx', headers);
		},(error)=>{this.showError(error)});
	}

	showPdfFile(attachment_id:number)
	{
		window.open(this.rest.getFilePath(attachment_id));
	}

	onShowUpdatePayment(payment_info:PaymentInfo)
	{
		this.selected_payment_info = payment_info;
		this.show_update_payment = true;
	}

	updatePaymentInfo(evt: Event)
	{
		evt.preventDefault();

		let bank_movement_list:Bank_Movement[] = this.selected_payment_info.movements.map((bmi)=> bmi.bank_movement );

		this.subs.sink = this.rest.bank_movement.update(bank_movement_list)
		.subscribe((response)=>
		{
			this.showSuccess('Pagos actualizados');
			this.show_update_payment = false;
			this.selected_payment_info = null;
		}, (error)=>this.showError(error));

	}
	removePayment(payment_info:PaymentInfo)
	{
		this.subs.sink = this.confirmation
		.showConfirmAlert
		(
			payment_info,
			'Cancelar Pago',
			'¿Estas seguro de cancelar este pago?'
		)
		.pipe
		(
			filter(result=>result.accepted),
			mergeMap((response:any)=>
			{
				this.is_loading = true;
				return this.rest.cancelPayment(response.obj.payment.id);
			}),
		)
		.subscribe
		({
			next:(response)=>
			{

				let index = this.payment_info_list.findIndex(pi => pi.payment.id === payment_info.payment.id);
				this.payment_info_list = this.payment_info_list.filter((pi, i) => i !== index);
				this.is_loading = false;
				this.showSuccess('El pago se cancelo correctamente')
			},
			error:(error)=>this.showError(error)
		});
	}
}
